import React, { useState } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import type { Pathway } from "../../types";
import { WhatCanIDoDialog } from "./WhatCanIDoDialog";
import { PathwayViewMoreDialog } from "./PathwayViewMoreDialog";
import { SentimentBadge } from "../../../sentiment/SentimentBadge";

interface PathwaySectionProps {
  pathways?: Pathway[];
  whatCanIDo?: unknown;
  onPathwayClick?: (pathwayId: string) => void;
}

const CARD_SIZE = 128;

const Row = styled(Box, {
  label: "PathwayRow",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
}));

const OVERLAY_CLASS = "PathwayCardOverlay-root";

const Card = styled(Box, {
  label: "PathwayCard",
})(() => ({
  position: "relative",
  width: CARD_SIZE,
  height: CARD_SIZE,
  borderRadius: 12,
  overflow: "hidden",
  flexShrink: 0,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  padding: 14,
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },
  // Lighten the dimming overlay on hover to reveal more of the pathway image
  // while keeping the overlay dark enough for the card text to stay readable.
  [`&:hover .${OVERLAY_CLASS}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
}));

const CardImage = styled("img", {
  label: "PathwayCardImage",
})(() => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  pointerEvents: "none",
}));

const CardOverlay = styled(Box, {
  label: "PathwayCardOverlay",
})(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  transition: "background-color 250ms ease-in-out",
}));

CardOverlay.defaultProps = {
  className: OVERLAY_CLASS,
};

const CardName = styled(Typography, {
  label: "PathwayCardName",
})(() => ({
  position: "relative",
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: "18px",
  color: "#ffffff",
  display: "-webkit-box",
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
}));

const MoreBadge = styled(Box, {
  label: "PathwayMoreBadge",
})(() => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  color: "#078dee",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  cursor: "pointer",
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "18px",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.08)",
  },
}));

const EmptyCard = styled(Box, {
  label: "PathwayEmptyCard",
})(() => ({
  width: CARD_SIZE,
  height: CARD_SIZE,
  borderRadius: 16,
  backgroundColor: "rgba(0, 0, 0, 0.48)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: 8,
}));

const EmptyText = styled(Typography, {
  label: "PathwayEmptyText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "22px",
  textAlign: "center",
}));

export const PathwaySection = ({
  pathways,
  whatCanIDo,
  onPathwayClick,
}: PathwaySectionProps) => {
  const [whatCanIDoOpen, setWhatCanIDoOpen] = useState(false);
  const [viewMoreOpen, setViewMoreOpen] = useState(false);

  if (!pathways || pathways.length === 0) {
    return (
      <Row>
        <EmptyCard>
          <EmptyText>No direct CTE Pathway</EmptyText>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setWhatCanIDoOpen(true)}
            sx={{
              color: "#ffffff",
              borderColor: "rgba(255, 255, 255, 0.48)",
              textTransform: "none",
              fontFamily: "Proxima Nova, sans-serif",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: "22px",
              borderRadius: "8px",
              minWidth: 64,
              height: 30,
              px: 1,
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.64)",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            What can I do?
          </Button>
        </EmptyCard>
        <WhatCanIDoDialog
          open={whatCanIDoOpen}
          onClose={() => setWhatCanIDoOpen(false)}
          richTextContent={whatCanIDo}
        />
      </Row>
    );
  }

  const visiblePathways = pathways.slice(0, 2);
  const extraCount = pathways.length - visiblePathways.length;

  return (
    <Row>
      {visiblePathways.map((pathway) => (
        <Card key={pathway.id} onClick={() => onPathwayClick?.(pathway.id)}>
          {pathway.displayImage?.url && (
            <CardImage
              src={
                pathway.displayImage.sizes?.hatCard?.url ||
                pathway.displayImage.url
              }
              alt={pathway.name}
            />
          )}
          <CardOverlay />
          <CardName>{pathway.name}</CardName>
          <SentimentBadge
            sentiment={pathway.sentiment}
            sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
          />
        </Card>
      ))}
      {extraCount > 0 && (
        <MoreBadge
          role="button"
          aria-label={`Show ${extraCount} more pathways`}
          onClick={() => setViewMoreOpen(true)}
        >
          +{extraCount}
        </MoreBadge>
      )}
      <PathwayViewMoreDialog
        open={viewMoreOpen}
        onClose={() => setViewMoreOpen(false)}
        pathways={pathways}
        onPathwayClick={onPathwayClick}
      />
    </Row>
  );
};
